import type { Severity } from "@shared/types.js";

/** Rule id -> short title, as shown in tables. */
export const RULE_TITLE: Record<string, string> = {
  "priced-without-cr": "Priced without a received CR",
  "received-not-priced": "CR received but not yet priced",
  "double-entry": "Both new rate and estimation entered",
  "estimated-though-received": "Estimated although the CR arrived",
  "missing-input-date": "Status Done but no input date",
  "date-without-status": "Input date filled but status not Done",
  "missing-received-date": "Marked received but no received date",
  "crosscheck-without-entry": "Crosschecked but nothing entered",
  "entered-not-crosschecked": "Entered but not crosschecked",
  "crosscheck-no-owner": "Crosscheck date without an owner",
  "exported-not-crosschecked": "Exported without a crosscheck",
  "uploaded-not-exported": "Uploaded but no export date",
  "sequence-anomaly": "Export date precedes crosscheck date",
  "missing-category": "Category missing",
  "unexpected-category": "Category outside A–E",
  "missing-code": "No supplier / product code",
  "missing-name": "Supplier name missing",
  "missing-region": "Region missing",
  "missing-selling-decision": "Priced without a selling decision",
  "missing-pic": "Entered without a PIC",
  "duplicate-record": "Duplicate record",
  "cat-e-overlap": "Supplier appears in two sheets"
};

/** Rule id -> plain-language explanation of why the row was flagged. */
export const RULE_DESC: Record<string, string> = {
  "priced-without-cr": "New Rate Input Status is Done, but the CR status is not 'Received New Rate' — either the rate was entered from a CR nobody logged, or the receive status was never updated.",
  "received-not-priced": "The contract rate has arrived but pricing has not been entered yet. This is the active pricing backlog.",
  "double-entry": "The same record is marked Done for both New Rate and Estimation Price — a likely double count.",
  "estimated-though-received": "An estimation was entered even though the CR was received; the record should be priced from the real rate instead.",
  "missing-input-date": "A status says Done but the matching input date column is empty, so the work cannot be dated.",
  "date-without-status": "An input date is filled but the status column was never set to Done.",
  "missing-received-date": "CR is marked as received but 'CR Received Date' is blank.",
  "crosscheck-without-entry": "A crosscheck date exists although neither the new rate nor an estimation has been entered.",
  "entered-not-crosschecked": "The record has been entered but no crosscheck date has been recorded yet.",
  "crosscheck-no-owner": "A crosscheck date is present but 'Crosschecked by' is empty.",
  "exported-not-crosschecked": "The record reached the extranet without ever being crosschecked.",
  "uploaded-not-exported": "Upload Date is filled but 'Export to Extranet Date' is empty.",
  "sequence-anomaly": "The export date is earlier than the crosscheck date, so the sequence of work does not add up.",
  "missing-category": "No Category value, so the record cannot be attributed to A–E in any breakdown.",
  "unexpected-category": "The category value is outside the expected A–E range.",
  "missing-code": "No supplier or product code, so the row has no unique identifier.",
  "missing-name": "The supplier name cell is empty.",
  "missing-region": "The region cell is empty, so the record drops out of regional filtering.",
  "missing-selling-decision": "Priced as a new rate but no selling decision was recorded.",
  "missing-pic": "Work is marked Done but no PIC was recorded against it.",
  "duplicate-record": "More than one row shares the same code and product within a sheet.",
  "cat-e-overlap": "The same supplier code exists in both the Accommodation sheet and the Cat. E sheet — counting both sheets double-counts this supplier."
};

/** Map a severity to its pill class. */
export function sevPill(sev: Severity | string): string {
  return sev === "high" ? "p-high" : sev === "medium" ? "p-med" : "p-low";
}
